import { Flex, Modal } from 'antd'
import { Dispatch, SetStateAction, useState } from 'react'
import styled from 'styled-components'
import useUnlockKolSnapshot from '@/components/kol/detail/hooks/use-unlock-kol-snapshot'
import { VipStyleButton } from '@/components/vip-button'
import useIsJapan from '@/hooks/use-is-japan'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { reduxAPI } from '@/store/redux-api'

type UseHandleClickUnlockButton = (
  from?: string,
  isClickedFromDrawer?: boolean,
) => {
  isVipOnlyModalOpen: boolean
  setIsVipOnlyModalOpen: Dispatch<SetStateAction<boolean>>
  isUnlocking: boolean
  handleClickUnlock: ReturnType<typeof useUnlockKolSnapshot>['handleUnlock']
}

const useHandleClickUnlockButton: UseHandleClickUnlockButton = (
  from,
  isClickedFromDrawer,
) => {
  const { formatMessage } = useIntl()

  const [isVipOnlyModalOpen, setIsVipOnlyModalOpen] = useState(false)

  const { withFreePermission } = usePermissions()
  const contactModal =
    reduxAPI.state.modal.hooks.useVisible('purchaseEnterprise')
  const isJapan = useIsJapan()

  const { handleUnlock, isUnlocking } = useUnlockKolSnapshot(
    () => {
      if (isJapan) {
        Modal.confirm({
          closable: true,
          okButtonProps: { hidden: true },
          cancelButtonProps: { hidden: true },
          icon: null,
          title: (
            <UnlockQuotaInsufficientTitle>
              {formatMessage({
                id: 'kol:description_kol_info_quota_not_enough_unable_purchases_quota',
              })}
            </UnlockQuotaInsufficientTitle>
          ),
        })
        return
      }

      setIsVipOnlyModalOpen(true)
    },
    from,
    isClickedFromDrawer,
  )

  const handleClickUnlock: ReturnType<UseHandleClickUnlockButton>['handleClickUnlock'] =
    async (kolInfo) => {
      if (isJapan && withFreePermission) {
        contactModal.toggleVisible()
        return false
      }

      if (kolInfo.isExpired) {
        Modal.confirm({
          centered: true,
          title: formatMessage({
            id: 'kol:statistic_insufficient_modal_title',
          }),
          content: formatMessage({
            id: 'kol:statistic_insufficient_modal_content',
          }),
          cancelText: formatMessage({ id: 'general:btn_text_cancel' }),
          footer: (_, { CancelBtn }) => {
            return (
              <Flex gap={7} justify='flex-end'>
                <CancelBtn />
                <VipStyleButton
                  type='primary'
                  onClick={() => {
                    handleUnlock(kolInfo)
                    Modal.destroyAll()
                  }}
                >
                  {formatMessage({ id: 'kol:continue_unlocking' })}
                </VipStyleButton>
              </Flex>
            )
          },
        })
        return false
      }

      return await handleUnlock(kolInfo)
    }

  return {
    isVipOnlyModalOpen,
    setIsVipOnlyModalOpen,
    isUnlocking,
    handleClickUnlock,
  }
}

const UnlockQuotaInsufficientTitle = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey2};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 1.4;
`

export default useHandleClickUnlockButton
